import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleServiceChange = (value) => setFormData({ ...formData, service: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    setLoading(true);
    try {
      await axios.post(`${API}/quote-request`, formData);
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-17938247612/bqgYCJOqufQbELzfz-lC' });
      }
      toast.success("Quote request sent successfully! We'll contact you soon.");
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      toast.error('Failed to send request. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-[#111640]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block bg-[#0066FF]/20 text-[#00D9FF] px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Request a Quote</h2>
          <p className="text-base md:text-lg text-gray-400">Fill out the form below and we'll get back to you within 24 hours</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <div>
            <Card className="bg-white/5 backdrop-blur-md border border-white/10">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                  {/* Honeypot - hidden from real users */}
                  <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <label htmlFor="website">Website</label>
                    <input type="text" id="website" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" />
                  </div>
                  {[
                    { label: 'Name', name: 'name', type: 'text', placeholder: 'Your full name' },
                    { label: 'Email', name: 'email', type: 'email', placeholder: 'your.email@example.com' },
                    { label: 'Phone', name: 'phone', type: 'tel', placeholder: '064 450 5026' },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">{field.label} *</label>
                      <Input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        required
                        placeholder={field.placeholder}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00D9FF]"
                        data-testid={`input-${field.name}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Service Required *</label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-service">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          ['aircon-servicing', 'Air Conditioning Servicing'],
                          ['aircon-repairs', 'Aircon Repairs'],
                          ['regassing', 'Regassing Services'],
                          ['fridge-repairs', 'Fridge Repairs'],
                          ['compressor-replacement', 'Compressor Replacement'],
                          ['split-installation', 'Split Unit Installation'],
                          ['cold-room', 'Cold Room Repairs'],
                          ['drain-blockage', 'Drain Blockage Repairs'],
                          ['other', 'Other'],
                        ].map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your requirements..."
                      rows={4}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00D9FF]"
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#0066FF] to-[#00D9FF] hover:from-[#0052CC] hover:to-[#00B8D4] text-white text-lg py-6 font-bold shadow-lg shadow-blue-500/25"
                    data-testid="submit-quote-btn"
                  >
                    {loading ? 'Sending...' : 'Send Request'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    By submitting, you agree to our{' '}
                    <a href="/privacy-policy" className="text-[#00D9FF] hover:text-white underline">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="/terms-of-service" className="text-[#00D9FF] hover:text-white underline">Terms of Service</a>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-6">Contact Information</h3>
              <div className="space-y-5">
                {[
                  { icon: <Phone className="w-5 h-5 text-[#00D9FF]" />, label: 'Phone', value: '064 450 5026', href: 'tel:0644505026' },
                  { icon: <Mail className="w-5 h-5 text-[#00D9FF]" />, label: 'Email', value: 'chillpointexec@gmail.com', href: 'mailto:chillpointexec@gmail.com' },
                  { icon: <MapPin className="w-5 h-5 text-[#00D9FF]" />, label: 'Service Area', value: 'Ballito & North Coast, KZN' },
                  { icon: <Clock className="w-5 h-5 text-[#00D9FF]" />, label: 'Hours', value: 'Mon-Fri: 8AM-5PM | Sat: 8AM-1PM', extra: 'Emergency: 24/7' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-[#00D9FF] hover:text-white transition-colors text-sm">{item.value}</a>
                      ) : (
                        <div className="text-gray-400 text-sm">{item.value}</div>
                      )}
                      {item.extra && <div className="text-red-400 font-semibold text-sm mt-1">{item.extra}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0066FF] to-[#00D9FF] p-8 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-3">Quick Contact</h3>
              <p className="mb-6 text-blue-100 text-sm">Need immediate assistance? Reach out directly:</p>
              <Button className="w-full bg-white text-[#0A0E27] hover:bg-gray-100 mb-3 font-bold" data-testid="quick-whatsapp-btn">
                <a href="https://wa.me/27644505026" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 w-full">
                  <Send className="w-4 h-4" />
                  <span>WhatsApp: 064 450 5026</span>
                </a>
              </Button>
              <Button className="w-full bg-white/20 border border-white/30 text-white hover:bg-white/30" data-testid="quick-call-btn">
                <a href="tel:0644505026" className="flex items-center justify-center space-x-2 w-full">
                  <Phone className="w-4 h-4" />
                  <span>Call Now</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
